import { useState, useEffect } from "react";
import {
  useQueryParams,
  ArrayParam,
  NumberParam,
  StringParam,
} from "use-query-params";
import { useDebounce } from "../../../../util/hooks";
import type { FiltersDropDownItem } from "../../../../components/FiltersDropDown/FiltersDropDown";
import { DEFAULT_PER_PAGE } from "../../../../util/util";

export interface ListPageStateConfig {
  defaultPerPage?: number;
  perPageOptions?: number[];
}

export interface ListPageState {
  // Query parameters
  query: {
    q: string | null | undefined;
    offset: number | null | undefined;
    perPage: number | null | undefined;
    status: (string | null)[] | null | undefined;
    account_managers: (string | null)[] | null | undefined;
    customer_service_reps: (string | null)[] | null | undefined;
  };
  setQuery: (params: Partial<ListPageState["query"]>) => void;

  // Search state
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  debouncedSearchQuery: string;

  // Pagination state
  offset: number;
  setOffset: (offset: number) => void;
  perPage: number;
  setPerPage: (perPage: number) => void;
  perPageItems: number[];

  // Table pagination
  tablePagination: {
    perPage: number;
    pageCount: number;
    pageIndex: number;
    totalCount: number;
  };
  setTablePagination: (pagination: ListPageState["tablePagination"]) => void;

  // Filter state
  selectedStatus: FiltersDropDownItem[];
  setSelectedStatus: (status: FiltersDropDownItem[]) => void;
  selectedAccountManagers: FiltersDropDownItem[];
  setSelectedAccountManagers: (managers: FiltersDropDownItem[]) => void;
  selectedCustomerServiceReps: FiltersDropDownItem[];
  setSelectedCustomerServiceReps: (reps: FiltersDropDownItem[]) => void;

  // Modal state
  showCreateModal: boolean;
  setShowCreateModal: (show: boolean) => void;
  showDialogBox: boolean;
  setShowDialogBox: (show: boolean) => void;
  downloadReportSlideOut: boolean;
  setDownloadReportSlideOut: (show: boolean) => void;
}

/**
 * Custom hook that manages common state for list pages
 */
export const useListPageState = (
  config: ListPageStateConfig = {}
): ListPageState => {
  const {
    defaultPerPage = DEFAULT_PER_PAGE,
    perPageOptions = [10, 20, 50, 100],
  } = config;

  const [query, setQuery] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
    status: ArrayParam,
    account_managers: ArrayParam,
    customer_service_reps: ArrayParam,
  });

  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [offset, setOffset] = useState(query?.offset ?? 0);
  const [perPage, setPerPage] = useState(query?.perPage ?? defaultPerPage);
  const [tablePagination, setTablePagination] = useState({
    perPage: defaultPerPage,
    pageCount: 0,
    pageIndex: 0,
    totalCount: 0,
  });

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState<FiltersDropDownItem[]>(
    []
  );
  const [selectedAccountManagers, setSelectedAccountManagers] = useState<
    FiltersDropDownItem[]
  >([]);
  const [selectedCustomerServiceReps, setSelectedCustomerServiceReps] =
    useState<FiltersDropDownItem[]>([]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDialogBox, setShowDialogBox] = useState(false);
  const [downloadReportSlideOut, setDownloadReportSlideOut] = useState(false);

  // Update query parameters when state changes
  useEffect(() => {
    if (!query.offset || offset !== query.offset) {
      setQuery({ offset });
    }
    if (!query.perPage || perPage !== query.perPage) {
      setQuery({ perPage });
    }
  }, [offset, perPage, query.offset, query.perPage, setQuery]);

  // Handle search query changes
  useEffect(() => {
    if (debouncedSearchQuery === "") {
      setQuery({ q: undefined });
    }
    if (debouncedSearchQuery) {
      setQuery({ q: debouncedSearchQuery });
    }
  }, [setQuery, query, debouncedSearchQuery]);

  return {
    query,
    setQuery,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    offset,
    setOffset,
    perPage,
    setPerPage,
    perPageItems: perPageOptions,
    tablePagination,
    setTablePagination,
    selectedStatus,
    setSelectedStatus,
    selectedAccountManagers,
    setSelectedAccountManagers,
    selectedCustomerServiceReps,
    setSelectedCustomerServiceReps,
    showCreateModal,
    setShowCreateModal,
    showDialogBox,
    setShowDialogBox,
    downloadReportSlideOut,
    setDownloadReportSlideOut,
  };
};
